import { Form } from './types';
import { BasicAlert } from '@g17eco/molecules/alert';
import { SubsidiarySelectionTable } from '@features/downloads/partials/SubsidiarySelectionTable';
import { SurveySelectionTable, TableNodeWithSurveyIds } from '@features/downloads/partials/SurveySelectionTable';
import { getSelectedSurveyNodeIds } from '@features/downloads/util/treeNavigation';
import { CollapseButton, CollapseContent, CollapsePanel } from '@g17eco/molecules/collapse-panel';
import {
  useGetSubsidiariesForCustomReportQuery,
  useGetSurveysByInitiativeIdsForCustomReportQuery,
} from '@api/custom-reports';
import { Loader } from '@g17eco/atoms/loader';

export interface ReportDataSourceProps {
  initiativeId: string;
  form: Pick<Form, 'initiativeIds' | 'surveyFilters'>;
  onChangeInitiativeIds: (initiativeIds: Form['initiativeIds']) => void;
  onChangeSurveyFilters: (surveyFilters: Form['surveyFilters']) => void;
}

export const ReportDataSource = ({
  initiativeId,
  form,
  onChangeInitiativeIds,
  onChangeSurveyFilters,
}: ReportDataSourceProps) => {
  const { initiativeIds, surveyFilters } = form;

  // Hold both panels behind a Loader until queries resolve. Without this,
  // the table-library row-select hooks fire onChange([]) against empty
  // data on mount, which bubbles through Configuration.handleChangeInitiativeIds
  // and wipes the saved surveyFilters before the user has done anything.
  // RTK Query dedupes with Configuration's identical subscriptions.
  const { data: subsidiaryList } = useGetSubsidiariesForCustomReportQuery(initiativeId, {
    skip: !initiativeId,
  });
  const allSubsidiaryIds = subsidiaryList?.map(({ _id }) => _id) ?? [];
  const { isLoading: surveysLoading, isUninitialized: surveysUninit } =
    useGetSurveysByInitiativeIdsForCustomReportQuery(
      { initiativeId, initiativeIds: allSubsidiaryIds },
      { skip: !initiativeId || allSubsidiaryIds.length === 0 }
    );
  // No subsidiaries → surveys query is skipped; don't gate on it.
  const dataReady =
    subsidiaryList !== undefined &&
    (allSubsidiaryIds.length === 0 || (!surveysLoading && !surveysUninit));

  const handleSelectedNode = (selectedNodes?: TableNodeWithSurveyIds[]) => {
    onChangeSurveyFilters(
      selectedNodes?.map(({ surveyType: type, period, effectiveDate }) => ({ type, period, effectiveDate })) ?? []
    );
  };

  return (
    <>
      <CollapsePanel className='mt-4 background-ThemeBgMedium' collapsed>
        <CollapseButton>
          <div className='pe-2 d-flex justify-content-between align-items-center'>
            <h6 className='fw-bold'>Select subsidiaries*</h6>
            <span className='text-ThemeTextMedium'>{initiativeIds.length} subsidiaries selected</span>
          </div>
        </CollapseButton>
        <CollapseContent className='p-2'>
          {!dataReady ? (
            <Loader relative />
          ) : (
            <SubsidiarySelectionTable
              initiativeId={initiativeId}
              selectedIds={initiativeIds}
              handleSetSelectedIds={onChangeInitiativeIds}
              hideCount
            />
          )}
        </CollapseContent>
      </CollapsePanel>
      <CollapsePanel className='mt-4 background-ThemeBgMedium' collapsed>
        <CollapseButton>
          <div className='pe-2 d-flex justify-content-between align-items-center'>
            <h6 className='fw-bold'>Date periods*</h6>
            <span className='text-ThemeTextMedium'>{surveyFilters.length} date periods selected</span>
          </div>
        </CollapseButton>
        <CollapseContent className='p-2'>
          {!dataReady ? (
            <Loader relative />
          ) : !initiativeIds.length ? (
            <BasicAlert type={'warning'}>Please select subsidiaries above</BasicAlert>
          ) : (
            <SurveySelectionTable
              isMulti
              initiativeId={initiativeId}
              initiativeIds={initiativeIds}
              handleSelectedNodes={handleSelectedNode}
              selectedNodeIds={getSelectedSurveyNodeIds(surveyFilters)}
              hideCount
            />
          )}
        </CollapseContent>
      </CollapsePanel>
    </>
  );
};
